import type { ComponentType, SVGProps } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils/cn';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useSession } from '@/hooks/useSession';
import { useSurahList } from '@/hooks/useSurah';
import {
  useBookmarksCount,
  useReadingProgress,
  useReadingStreak,
  useReadsByAllSurahs,
  useTotalAyahReads,
} from '@/hooks/useStudentStats';
import { signOut } from '@/lib/auth/session';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  IconArrowRight,
  IconBookmark,
  IconBookOpen,
  IconFlame,
  IconLogOut,
  IconRepeat,
  IconSearch,
  IconSparkle,
  IconStar,
  IconTrendUp,
} from '@/components/home/icons';

/**
 * Student Portal — signed-in landing page.
 *
 * Every visible stat is backed by a real query against the user's
 * own rows (RLS enforces the user_id boundary). When a query is in
 * flight we render a skeleton; when it returns 0 we render a calm
 * zero-state CTA instead of a fake number.
 *
 * Data sources:
 *   useTotalAyahReads()     → count(ayah_reads)
 *   useReadingProgress()    → reading_progress single row
 *   useReadsByAllSurahs()   → group-by surah_id → per-surah heatmap
 *   useReadingStreak()      → distinct read days → current/longest streak
 *   useBookmarksCount()     → count(bookmarks)
 */

type IconCmp = ComponentType<SVGProps<SVGSVGElement>>;

type ActionTile = {
  to: string;
  Icon: IconCmp;
  label: string;
  description: string;
  tone: 'beam' | 'beam-soft' | 'amber' | 'emerald';
};

const ACTION_TILES: ActionTile[] = [
  { to: '/surah', Icon: IconBookOpen, label: 'Open the Quran', description: 'Browse all 114 surahs.', tone: 'beam' },
  { to: '/reader', Icon: IconSparkle, label: 'Focused Reader', description: 'One ayah at a time with recitation.', tone: 'beam-soft' },
  { to: '/memorize', Icon: IconRepeat, label: 'Memorize', description: 'Repeat, hide, recall.', tone: 'emerald' },
  { to: '/search', Icon: IconSearch, label: 'Search', description: 'Find ayahs by meaning or keyword.', tone: 'beam' },
  { to: '/bookmarks', Icon: IconBookmark, label: 'Bookmarks', description: 'Ayahs you saved for later.', tone: 'amber' },
  { to: '/find-qari', Icon: IconStar, label: 'Qari Services', description: 'Browse verified Quran teachers.', tone: 'amber' },
];

function toneClasses(tone: ActionTile['tone']) {
  switch (tone) {
    case 'beam': return 'text-beam border-beam/30 bg-beam/10';
    case 'beam-soft': return 'text-beam-soft border-beam-soft/30 bg-beam-soft/10';
    case 'emerald': return 'text-[#4DA088] border-[#4DA088]/30 bg-[#4DA088]/10';
    case 'amber': return 'text-amber-400 border-amber-400/30 bg-amber-400/10';
  }
}

/* ------------------------------------------------------------------ */
/* Atoms                                                              */
/* ------------------------------------------------------------------ */

function StatCard({
  Icon,
  label,
  value,
  unit,
  loading,
  iconColor = 'text-beam',
}: {
  Icon: IconCmp;
  label: string;
  value: number | null;
  unit: string;
  loading: boolean;
  iconColor?: string;
}) {
  return (
    <div className="flex flex-col rounded-2xl border border-night-500/20 bg-night-800 p-5">
      <div className="flex items-center justify-between">
        <Icon className={cn('h-5 w-5', iconColor)} aria-hidden="true" />
      </div>
      <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-night-200">
        {label}
      </p>
      {loading ? (
        <Skeleton className="mt-2 h-9 w-20" />
      ) : (
        <p className="mt-1 font-display text-3xl font-bold text-night-50 tabular-nums">
          {value ?? 0}
        </p>
      )}
      <p className="mt-1 text-xs text-night-200">{unit}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                               */
/* ------------------------------------------------------------------ */

export function StudentPortal() {
  useDocumentTitle('Student Portal');
  const session = useSession();

  const email = session.data?.user.email ?? '';
  const displayName =
    (session.data?.user.user_metadata?.full_name as string | undefined) ??
    email.split('@')[0] ??
    'Student';

  const totalReads = useTotalAyahReads();
  const progress = useReadingProgress();
  const streak = useReadingStreak();
  const bySurah = useReadsByAllSurahs();
  const bookmarkCount = useBookmarksCount();
  const surahList = useSurahList();

  // Resolve the continue-reading surah name from the surah list.
  const continueSurah = progress.data
    ? surahList.data?.find((s) => s.id === progress.data?.last_surah_id) ?? null
    : null;

  async function handleSignOut() {
    try {
      await signOut();
    } catch {
      // Non-fatal; useSession picks up SIGNED_OUT eventually.
    }
  }

  return (
    <div className="relative min-h-screen bg-night-900 text-night-50">
      <div aria-hidden="true" className="pointer-events-none fixed -right-24 top-1/4 hidden h-96 w-96 rounded-full bg-beam/5 blur-[120px] sm:block" />
      <div aria-hidden="true" className="pointer-events-none fixed bottom-0 left-0 hidden h-[500px] w-[500px] rounded-full bg-beam-soft/5 blur-[150px] sm:block" />

      <section className="relative mx-auto max-w-5xl space-y-10 p-4 sm:space-y-12 sm:p-6 lg:p-12">
        {/* Header */}
        <header className="space-y-3 sm:space-y-4">
          <span className="block text-xs font-semibold uppercase tracking-[0.22em] text-beam-soft">
            Student Portal
          </span>
          <h1 className="font-display text-3xl font-bold tracking-tight text-night-50 sm:text-4xl lg:text-5xl">
            Salaam, <span className="text-beam">{displayName}</span>
          </h1>
          {email && (
            <p className="text-sm text-night-200 sm:text-base">
              Signed in as <span className="text-night-100">{email}</span>
            </p>
          )}
        </header>

        {/* Continue Reading — appears only if the user has opened a surah */}
        {progress.isLoading ? (
          <div className="rounded-2xl border border-night-500/20 bg-night-800 p-5">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="mt-3 h-6 w-48" />
          </div>
        ) : progress.data && continueSurah ? (
          <Link
            to={'/surah/' + progress.data.last_surah_id + '/ayah/' + progress.data.last_ayah_number}
            className="group flex items-center gap-4 rounded-2xl border border-night-500/20 bg-night-700 p-5 transition-all hover:border-beam/40 hover:bg-night-600"
          >
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-beam/30 bg-beam/10 text-beam">
              <IconBookOpen className="h-5 w-5" aria-hidden="true" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-beam-soft">
                Continue Reading
              </p>
              <p className="mt-1 font-display text-lg font-bold text-night-50">
                {continueSurah.name_en}
              </p>
              <p className="text-xs text-night-200">
                Ayah {progress.data.last_ayah_number} of {continueSurah.ayah_count}
              </p>
            </div>
            <IconArrowRight className="h-5 w-5 text-night-300 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-beam" aria-hidden="true" />
          </Link>
        ) : null}

        {/* Stat row — total reads / streak / longest streak / bookmarks */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-5">
          <StatCard
            Icon={IconTrendUp}
            label="Ayahs Read"
            value={totalReads.data ?? 0}
            unit="Total"
            loading={totalReads.isLoading}
          />
          <StatCard
            Icon={IconFlame}
            label="Current Streak"
            value={streak.data?.current ?? 0}
            unit={(streak.data?.current ?? 0) === 1 ? 'Day' : 'Days'}
            loading={streak.isLoading}
            iconColor="text-amber-400"
          />
          <StatCard
            Icon={IconStar}
            label="Longest Streak"
            value={streak.data?.longest ?? 0}
            unit={(streak.data?.longest ?? 0) === 1 ? 'Day' : 'Days'}
            loading={streak.isLoading}
            iconColor="text-beam-soft"
          />
          <StatCard
            Icon={IconBookmark}
            label="Bookmarks"
            value={bookmarkCount.data ?? 0}
            unit="Saved"
            loading={bookmarkCount.isLoading}
            iconColor="text-amber-400"
          />
        </div>

        {/* Per-surah progress — 10 circular chips per row. Each chip is
            a ring whose fill shows the read fraction; not-started shows
            an empty ring, complete shows a solid disc. */}
        <div className="rounded-2xl border border-night-500/20 bg-night-800 p-5">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-xs font-semibold uppercase tracking-[0.22em] text-night-200">
              Progress by Surah
            </h3>
            <span className="text-[10px] text-night-400">114 surahs</span>
          </div>
          {bySurah.isLoading || surahList.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : (
            <div className="grid grid-cols-10 gap-2 sm:gap-3">
              {(surahList.data ?? []).map((s) => (
                <SurahProgressChip
                  key={s.id}
                  id={s.id}
                  name={s.name_en}
                  ayahCount={s.ayah_count}
                  read={bySurah.data?.get(s.id) ?? 0}
                />
              ))}
            </div>
          )}
        </div>

        {/* Action tiles */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 sm:gap-5">
          {ACTION_TILES.map((tile) => (
            <Link
              key={tile.to}
              to={tile.to}
              className="group flex items-start gap-4 rounded-2xl border border-night-500/20 bg-night-800 p-5 transition-colors hover:border-night-500/40 hover:bg-night-700"
            >
              <span
                className={cn(
                  'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border',
                  toneClasses(tile.tone),
                )}
                aria-hidden="true"
              >
                <tile.Icon className="h-5 w-5" />
              </span>
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-base font-bold text-night-50">
                  {tile.label}
                </h3>
                <p className="mt-1 text-xs leading-relaxed text-night-200">
                  {tile.description}
                </p>
              </div>
              <IconArrowRight className="mt-1 h-4 w-4 shrink-0 text-night-400 transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-night-100" aria-hidden="true" />
            </Link>
          ))}
        </div>

        {/* Account row */}
        <div className="flex flex-col gap-3 rounded-2xl border border-night-500/20 bg-night-800 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="font-display text-sm font-bold text-night-50">Account</h3>
            <p className="mt-1 text-xs text-night-200">
              Manage your reading preferences and account.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/settings"
              className="rounded-lg border border-night-500/30 px-4 py-2 text-xs font-semibold text-night-100 transition-colors hover:bg-night-700"
            >
              Settings
            </Link>
            <button
              type="button"
              onClick={handleSignOut}
              className="inline-flex items-center gap-2 rounded-lg border border-night-500/30 px-4 py-2 text-xs font-semibold text-night-100 transition-colors hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-300"
            >
              <IconLogOut className="h-4 w-4" aria-hidden="true" />
              Sign out
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* SurahProgressChip                                                  */
/*                                                                    */
/* Circular progress indicator for a single surah.                    */
/*   - 0%  → empty ring (no fill, faint stroke)                       */
/*   - 1-99% → arc traced clockwise from the top in beam              */
/*   - 100% → fully filled disc, surah number inverted                */
/*                                                                    */
/* SVG circumference math:                                            */
/*   circumference = 2 * PI * r                                       */
/*   dashOffset = circumference * (1 - pct)                           */
/* The circle is rotated -90deg so the arc starts at 12 o'clock.      */
/* ------------------------------------------------------------------ */

function SurahProgressChip({
  id,
  name,
  ayahCount,
  read,
}: {
  id: number;
  name: string;
  ayahCount: number;
  read: number;
}) {
  const pct = ayahCount > 0 ? Math.min(read / ayahCount, 1) : 0;
  const isComplete = pct >= 1;
  const isStarted = pct > 0;

  const size = 36;
  const strokeWidth = 2.5;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - pct);

  return (
    <Link
      to={'/surah/' + id}
      title={name + ' — ' + read + ' / ' + ayahCount + ' ayahs read'}
      aria-label={
        name +
        ', ' +
        read +
        ' of ' +
        ayahCount +
        ' ayahs read (' +
        Math.round(pct * 100) +
        '%)'
      }
      className="group relative inline-flex items-center justify-center"
    >
      <svg
        width={size}
        height={size}
        viewBox={'0 0 ' + size + ' ' + size}
        className="-rotate-90 transition-transform group-hover:scale-110"
        aria-hidden="true"
      >
        {/* Empty background ring — always present, sets the 'not started' look. */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill={isComplete ? 'rgb(0 229 255 / 0.85)' : 'transparent'}
          stroke="rgb(100 116 139 / 0.35)"
          strokeWidth={strokeWidth}
        />
        {/* Progress arc — only rendered when something has been read.
            Stops being drawn at 100% because the fill above takes over. */}
        {isStarted && !isComplete && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="rgb(0 229 255)"
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            strokeLinecap="round"
            className="transition-[stroke-dashoffset] duration-500"
          />
        )}
      </svg>
      <span
        className={cn(
          'absolute inset-0 flex items-center justify-center text-[10px] font-semibold tabular-nums',
          isComplete
            ? 'text-beam-ink'
            : isStarted
              ? 'text-beam'
              : 'text-night-300',
        )}
      >
        {id}
      </span>
    </Link>
  );
}
